import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// One-time cleanup of the legacy localStorage token (pre cookie-auth clients)
try { localStorage.removeItem("tripmail_access_token"); } catch {}

export function getCsrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

const MUTATING = new Set(["post", "put", "patch", "delete"]);

api.interceptors.request.use((config) => {
  if (MUTATING.has((config.method || "").toLowerCase())) {
    const t = getCsrfToken();
    if (t) config.headers["X-CSRF-Token"] = t;
  }
  return config;
});

let refreshPromise = null;

export function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE}/auth/refresh`, null, { withCredentials: true })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

const NO_RETRY = ["/auth/login", "/auth/register", "/auth/refresh"];

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { config, response } = error;
    if (!response || !config || config._retried) return Promise.reject(error);
    const url = config.url || "";
    if (NO_RETRY.some((p) => url.includes(p))) return Promise.reject(error);
    const csrfFail = response.status === 403 && String(response.data?.detail || "").includes("CSRF");
    if (response.status !== 401 && !csrfFail) return Promise.reject(error);
    try {
      await refreshSession();
    } catch {
      return Promise.reject(error);
    }
    config._retried = true;
    if (MUTATING.has((config.method || "").toLowerCase())) {
      config.headers = { ...config.headers, "X-CSRF-Token": getCsrfToken() };
    }
    return api(config);
  }
);

// Raw fetch with cookie auth + CSRF, and a one-shot silent refresh on 401/403
// (for SSE streams and multipart uploads that can't go through axios).
export async function fetchWithAuth(url, options = {}, retried = false) {
  const res = await fetch(url, {
    credentials: "include",
    ...options,
    headers: { ...(options.headers || {}), "X-CSRF-Token": getCsrfToken() },
  });
  if ((res.status === 401 || res.status === 403) && !retried) {
    try {
      await refreshSession();
    } catch {
      return res;
    }
    return fetchWithAuth(url, options, true);
  }
  return res;
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
